import { useEffect } from 'react';

import { isBrowser } from '../../../core/utils/isBrowser.js';
import {
  getOverlayStackSize,
  subscribeToOverlayStack,
} from '../../../overlays/hooks/useOverlay.js';

/**
 * Auto-close behavior shared between `Menu` and `Menu.Sub`:
 *
 * - Closes on window blur, working around a Radix bug where submenus stay
 *   open when the browser window loses focus
 *   (https://github.com/radix-ui/primitives/issues/1441).
 * - Closes when any `useOverlay`-managed overlay (Modal, Sheet, Drawer, ...)
 *   is pushed onto the stack while the menu is open, so the user only has
 *   to dismiss the topmost layer.
 *
 * Menus themselves don't register with `visibleOverlays` (Radix has its own
 * dismiss layer); the hook just observes stack growth above the size
 * captured at open-time.
 * @internal
 */
export function useMenuAutoClose(
  open: boolean | undefined,
  setOpen: (open: boolean) => void,
): void {
  useEffect(() => {
    if (!isBrowser) {
      return undefined;
    }
    const onWindowBlur = () => setOpen(false);
    window.addEventListener('blur', onWindowBlur);
    return () => {
      window.removeEventListener('blur', onWindowBlur);
    };
  }, [setOpen]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const stackSizeAtOpen = getOverlayStackSize();
    return subscribeToOverlayStack(() => {
      if (getOverlayStackSize() > stackSizeAtOpen) {
        setOpen(false);
      }
    });
  }, [open, setOpen]);
}
